import { Inline, Text, vars } from '@etg/wings';
import { useDateFormat, useSearch, useTranslation } from '@eti/providers';
import type { CurrencyAmount } from '@eti/schema-types';
import { css, resetButtonStyle } from '@eti/styles';
import { date, price as priceUtils } from '@eti/utils';
import { type MutableRefObject, useEffect, useRef, useState } from 'react';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
  DEPARTURE_DATE,
  RETURN_DATE,
  SINGLE_BOUND,
} from '../../../../../../widgets/search-form/constants/formFields';
import { useSearchRedirectConfig } from '../../../../../../widgets/search-form/utils/searchRedirect';
import { handleOnSubmit } from '../../../../../../utils/searchFormSubmit';
import type { SearchFormValues } from '../../../../../../common/search-form-panel/SearchFormNewContainer';
import Skeleton from '../../../../../../common/components/Skeleton';
import { convertDateStringOrTimestamp } from '../../../../utils/dateUtils';

const getButtonStyles = (isSelected: Boolean) => css`
  ${resetButtonStyle}
  background-color: #fff;
  border-right: 1px solid ${vars.colors.divider};
  font-size: 0.875rem;
  height: 70px;
  width: 100%;

  :first-child {
    border-bottom-left-radius: ${vars.button.borderRadius};
    border-top-left-radius: ${vars.button.borderRadius};
  }

  :last-child {
    border-bottom-right-radius: ${vars.button.borderRadius};
    border-right: 0;
    border-top-right-radius: ${vars.button.borderRadius};
  }

  :hover {
    background-color: ${vars.colors.inputs.hover};
  }

  ${isSelected &&
  `border: 2px solid ${vars.colors.button.secondary.default.border};
    border-radius: 4px;
    position: relative;
    left: -1px;
    background-color: ${vars.colors.inputs.hover};
  `}
`;

const dateStyles = css`
  margin-bottom: 8px;
`;

const cheapestPriceStyles = css`
  color: #2d7e33;
  font-weight: 700;
`;

export type NearbyDatePriceProps = {
  searchFormWatchRef: MutableRefObject<UseFormWatch<SearchFormValues> | null>;
  setValueRef: MutableRefObject<UseFormSetValue<SearchFormValues> | null>;
  departureDate: string;
  handleSearch: (keepFilters: boolean) => void;
  isCheapest: boolean;
  isSelected: boolean;
  price?: CurrencyAmount;
  returnDate?: string | null;
  isLoading: boolean;
};

const NearbyDatePrice = ({
  isLoading,
  setValueRef,
  searchFormWatchRef,
  departureDate,
  handleSearch,
  isCheapest,
  isSelected,
  price,
  returnDate,
}: NearbyDatePriceProps) => {
  const { t } = useTranslation();
  const { day } = useDateFormat();
  const { currentSearchData, setSearchData } = useSearch();
  const searchRedirectConfig = useSearchRedirectConfig();
  const [nearbyDatesClicked, setNearbyDatesClicked] = useState(false);
  const travelerTexts = {
    adult: t('Adult'),
    adults: t('Adults'),
    child: t('Child'),
    children: t('Children'),
    infant: t('Infant'),
    infants: t('Infants'),
  };

  const handleClick = () => {
    const setValue = setValueRef?.current;
    if (setValue) {
      setValue(
        `${SINGLE_BOUND}.${DEPARTURE_DATE}`,
        new Date(convertDateStringOrTimestamp(departureDate)),
      );
      setValue(
        `${SINGLE_BOUND}.${RETURN_DATE}`,
        returnDate ? new Date(convertDateStringOrTimestamp(returnDate)) : '',
      );
    }
    setNearbyDatesClicked(true);
  };

  const currentDepartureDate =
    searchFormWatchRef &&
    searchFormWatchRef.current &&
    searchFormWatchRef.current().singleBound.departureDate;
  const previousValueRef = useRef(currentDepartureDate);

  useEffect(() => {
    if (previousValueRef.current !== currentDepartureDate && nearbyDatesClicked) {
      const searchFormWatchRefCurrent =
        searchFormWatchRef && searchFormWatchRef.current && searchFormWatchRef.current();
      handleOnSubmit(
        () => handleSearch(false),
        travelerTexts,
        currentSearchData,
        setSearchData,
        searchRedirectConfig,
      )(searchFormWatchRefCurrent);
      setNearbyDatesClicked(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDepartureDate]);

  return (
    <button
      className={getButtonStyles(isSelected)}
      data-testid={isSelected ? 'nearby-dates-current-selection' : 'nearby-dates-button'}
      onClick={handleClick}
      type="button"
    >
      <Inline align="center" className={dateStyles}>
        {isLoading ? (
          <Skeleton.Box height={18} width={50} />
        ) : (
          <Text data-testid="nearby-dates">
            {date.format(departureDate, day)}
            {returnDate && ` – ${date.format(returnDate, day)}`}
          </Text>
        )}
      </Inline>
      <Inline align="center">
        {isLoading ? (
          <Skeleton.Box height={18} width={50} />
        ) : (
          <Text className={isCheapest && cheapestPriceStyles} data-testid="nearby-dates-price">
            {priceUtils.formatPrice(price?.value ?? 0)}
          </Text>
        )}
      </Inline>
    </button>
  );
};

export default NearbyDatePrice;
